import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { useScrollReveal } from '../hooks/use-scroll-reveal';
import heroImage from '../assets/hero-ai-transition.jpg';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-4 mb-8">
    <span className="h-px w-12 bg-primary" aria-hidden="true" />
    <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
      {children}
    </p>
  </div>
);

const Hero = () => {
  const content = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative pt-20 pb-10 sm:pt-44 sm:pb-32 overflow-hidden bg-foreground">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt=""
          className="w-full h-full object-cover object-center opacity-35"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-foreground/75" />
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 15% 80%, hsl(var(--primary) / 0.22) 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div
          ref={content.ref}
          className={`max-w-4xl transition-all duration-1000 ${
            content.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              AI Transformation
            </p>
          </div>
          <h1
            className="text-background text-[2.25rem] sm:text-6xl lg:text-[5.5rem] leading-[1.05] mb-6 sm:mb-10"
            style={{ textWrap: 'balance' }}
          >
            Only a person can be <span className="text-primary italic">trustworthy.</span>
          </h1>
          <p className="text-background/85 text-base sm:text-xl leading-relaxed font-normal max-w-2xl">
            Every leader now makes a threefold call, over and over.
          </p>
        </div>

        <div
          ref={tail.ref}
          className={`mt-12 sm:mt-20 grid md:grid-cols-3 gap-4 sm:gap-6 transition-all duration-1000 delay-200 ${
            tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {[
            { n: '01', label: 'Delegate to people', body: 'so they grow.' },
            { n: '02', label: 'Delegate to machines', body: 'so the work flows.' },
            { n: '03', label: 'Keep human', body: 'what only a person can hold.' },
          ].map((t) => (
            <div
              key={t.n}
              className="border border-background/15 bg-background/[0.03] backdrop-blur-sm p-6 sm:p-7"
            >
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-4">
                {t.n}
              </p>
              <p className="text-background font-display text-2xl sm:text-3xl leading-tight mb-2">
                {t.label}
              </p>
              <p className="text-background/70 text-base leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TheStall = () => {
  const stat = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="bg-background py-16 sm:py-36">
      <div className="container-mobile max-w-6xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div
            ref={stat.ref}
            className={`md:col-span-5 transition-all duration-1000 ${
              stat.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Eyebrow>The Stall</Eyebrow>
            <div className="relative">
              <p
                className="font-display text-foreground tracking-tight leading-[1] mb-8"
                style={{ fontSize: 'clamp(5rem, 12vw, 8rem)' }}
              >
                95<span className="text-primary align-super text-[0.4em] ml-1">%</span>
              </p>
              <span className="block h-px w-16 bg-primary mb-6" aria-hidden="true" />
              <p className="text-foreground/85 text-sm sm:text-base tracking-[0.12em] uppercase leading-relaxed max-w-[30ch] md:max-w-[22ch]">
                of enterprise AI initiatives fail to reach production value
              </p>
            </div>
          </div>
          <div
            ref={body.ref}
            className={`md:col-span-7 md:pl-10 md:border-l md:border-border transition-all duration-1000 delay-150 ${
              body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2
              className="text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-8"
              style={{ textWrap: 'balance' }}
            >
              Deployment dies at <span className="text-primary italic">adoption.</span>
            </h2>
            <div className="space-y-6 text-lg sm:text-xl text-foreground/90 leading-relaxed">
              <p>
                The workflows get mapped. The agents get built. Then the initiative quietly stalls where the human beings are. The person asked to hand twenty years of judgment to a system she suspects will replace her.
              </p>
              <p>
                These moments never appear on the technical plan. They decide whether the system ever gets used.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TheWork = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const loopOne = useScrollReveal<HTMLDivElement>();
  const loopTwo = useScrollReveal<HTMLDivElement>();
  const footer = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary py-16 sm:py-36 overflow-hidden">
      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`grid md:grid-cols-12 gap-8 md:gap-12 mb-16 sm:mb-24 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="md:col-span-5">
            <Eyebrow>The Work</Eyebrow>
            <h2
              className="text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
              style={{ textWrap: 'balance' }}
            >
              One engagement. <span className="text-primary italic">Two loops.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-2">
            <p className="text-foreground/85 text-lg leading-relaxed">
              AI transformation runs on two loops at once. Most engagements only build the first. We work both. That is how the investment survives contact with your people.
            </p>
          </div>
        </div>

        {/* Loop 01 - light row */}
        <div
          ref={loopOne.ref}
          className={`relative bg-background border border-border/60 transition-all duration-1000 delay-100 ${
            loopOne.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 p-8 sm:p-12">
            <div className="md:col-span-3 flex md:block items-baseline gap-4">
              <p
                className="font-display text-primary leading-none"
                style={{ fontSize: 'clamp(3.5rem, 7vw, 5.5rem)' }}
              >
                01
              </p>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mt-3">
                Extraction
              </p>
            </div>
            <div className="md:col-span-9">
              <p className="font-display text-foreground text-2xl sm:text-3xl leading-tight mb-4">
                Where intelligence belongs.
              </p>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed mb-6">
                The workflows, decisions, and interfaces where AI creates real leverage in your operation. We map where the machine belongs and where it does not.
              </p>
              <div className="pt-5 border-t border-border/60">
                <p className="text-foreground/70 text-sm tracking-wide uppercase mb-2">
                  Built with partners
                </p>
                <p className="text-foreground/85 leading-relaxed">
                  We work alongside your technical team, or with builders we trust.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Connector rail */}
        <div className="flex justify-center py-4 sm:py-6" aria-hidden="true">
          <span className="h-10 w-px bg-primary/40" />
        </div>

        {/* Loop 02 - dark row */}
        <div
          ref={loopTwo.ref}
          className={`relative bg-foreground text-background transition-all duration-1000 delay-200 ${
            loopTwo.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 p-8 sm:p-12">
            <div className="md:col-span-3 flex md:block items-baseline gap-4">
              <p
                className="font-display text-primary leading-none"
                style={{ fontSize: 'clamp(3.5rem, 7vw, 5.5rem)' }}
              >
                02
              </p>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mt-3">
                Development
              </p>
            </div>
            <div className="md:col-span-9">
              <p className="font-display text-background text-2xl sm:text-3xl leading-tight mb-4">
                Where the organization will resist.
              </p>
              <p className="text-background/80 text-base sm:text-lg leading-relaxed mb-6">
                The identity, trust, and judgment fractures that quietly kill adoption. This is where human capacity gets built and where the investment survives.
              </p>
              <div className="pt-5 border-t border-background/20">
                <p className="text-background/70 text-sm tracking-wide uppercase mb-2">
                  Development inside deployment
                </p>
                <p className="text-background/85 leading-relaxed">
                  Deliberate practices threaded into the rollout. Your people grow the judgment to run the business alongside the system, while it goes live.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Essay links */}
        <div
          ref={footer.ref}
          className={`mt-14 sm:mt-20 pt-8 border-t border-border grid sm:grid-cols-2 gap-6 sm:gap-10 transition-all duration-1000 delay-300 ${
            footer.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Link
            to="/essays/the-second-loop"
            onClick={() => window.scrollTo(0, 0)}
            className="group/link flex items-start gap-4 text-foreground hover:text-primary transition-colors"
          >
            <span className="text-primary font-semibold tracking-[0.2em] uppercase text-xs pt-2 whitespace-nowrap">
              Essay 01
            </span>
            <span className="flex-1">
              <span className="block font-display text-lg sm:text-xl leading-snug border-b border-foreground/20 group-hover/link:border-primary pb-2 transition-colors">
                The Second Loop
              </span>
            </span>
            <ArrowRight className="h-4 w-4 mt-3 group-hover/link:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/essays/the-space-between"
            onClick={() => window.scrollTo(0, 0)}
            className="group/link flex items-start gap-4 text-foreground hover:text-primary transition-colors"
          >
            <span className="text-primary font-semibold tracking-[0.2em] uppercase text-xs pt-2 whitespace-nowrap">
              Essay 02
            </span>
            <span className="flex-1">
              <span className="block font-display text-lg sm:text-xl leading-snug border-b border-foreground/20 group-hover/link:border-primary pb-2 transition-colors">
                The Space Between
              </span>
            </span>
            <ArrowRight className="h-4 w-4 mt-3 group-hover/link:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-16 sm:py-40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-normal max-w-xl mx-auto">
          One conversation is where it begins.
        </p>
        <div className="pt-2">
          <Button
            onClick={handleScheduleCall}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
          >
            Begin a Conversation
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const AITransition = () => {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI Transformation: The Human Side of AI Deployment',
      provider: { '@type': 'Organization', name: 'ParadoxEdge', url: 'https://www.paradoxedge.com' },
      serviceType: ['AI Transformation', 'AI Adoption', 'Change Management', 'Leadership Development', 'Organizational Development'],
      description:
        'The human side of AI deployment. Dual-loop audits, development inside deployment, and judgment for the space between people and machines.',
      url: 'https://www.paradoxedge.com/ai-transformation',
      areaServed: 'Worldwide',
      audience: { '@type': 'BusinessAudience', audienceType: 'Companies bringing AI into their work' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.paradoxedge.com/' },
        { '@type': 'ListItem', position: 2, name: 'AI Transformation', item: 'https://www.paradoxedge.com/ai-transformation' },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="AI Transformation | The Human Side of AI Deployment | ParadoxEdge"
        description="Most AI initiatives stall at adoption, where the human beings are. ParadoxEdge works the human side of AI deployment so the investment survives contact with your people."
        canonicalUrl="/ai-transformation"
        keywords="AI adoption, AI deployment, change management, human side of AI, AI transformation, leadership development"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <ElfsightWidget />
        <main>
          <Hero />
          <TheStall />
          <TheWork />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AITransition;